import {addDays, isAfter, isBefore} from 'date-fns'

import {nextJobDay} from 'src/jobs/job-entry/job-entry-logic'
import {parseApiDate, parseApiTime} from 'src/jobs/job-schedule-format'
import type {JobDetail} from 'src/jobs/schemas'
import type {CreateServiceScheduleInput} from 'src/service-schedules/schemas'
import {jsDayToPythonWeekday} from 'src/service-schedules/weekday-conversion'

export type VisitLine = {
    kind: 'worker' | 'equipment'
    typeId: string
    name: string
    quantity: number
}

export type ServiceVisitsValues = {
    serviceTypeId: string | null
    // Python weekday numbers: Monday = 0 to Sunday = 6.
    weekdays: number[]
    startDate: Date | null
    endDate: Date | null
    visitTime: string | null
    notes: string
    lines: VisitLine[]
    readyForDispatch: boolean
}

export const MAX_VISITS = 200

export const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

/**
 * Build visit form values from a job: start the day after the job, end on its planned end date,
 * and default equipment quantities to 0.
 */
export function serviceVisitsFromJob(job: JobDetail, today: Date): ServiceVisitsValues {
    const startDate = nextJobDay(job, today)
    const plannedEnd = job.plannedEndDate ? parseApiDate(job.plannedEndDate) : null
    return {
        serviceTypeId: job.serviceTypeId,
        weekdays: [],
        startDate,
        endDate: plannedEnd && !isBefore(plannedEnd, startDate) ? plannedEnd : null,
        visitTime: parseApiTime(job.startTime),
        notes: job.notes ?? '',
        lines: mergeVisitLinesByType([
            ...job.workerRequirements.map((requirement) => ({
                kind: 'worker' as const,
                typeId: requirement.workerTypeId,
                name: requirement.workerTypeName,
                quantity: requirement.quantity,
            })),
            ...job.assetRequirements.map((requirement) => ({
                kind: 'equipment' as const,
                typeId: requirement.rentalAssetTypeId,
                name: requirement.rentalAssetTypeName,
                quantity: 0,
            })),
        ]),
        readyForDispatch: true,
    }
}

function mergeVisitLinesByType(lines: VisitLine[]): VisitLine[] {
    const merged: VisitLine[] = []
    for (const line of lines) {
        const same = merged.find((kept) => kept.kind === line.kind && kept.typeId === line.typeId)
        if (same) {
            same.quantity += line.quantity
        } else {
            merged.push({...line})
        }
    }
    return merged
}

export function visitsAddedTitle(count: number, orderLabel: string): string {
    return `${count} service ${count === 1 ? 'visit' : 'visits'} added to order ${orderLabel}`
}

/** Dates between start and end (inclusive) that fall on the selected weekdays. */
export function visitDays(values: Pick<ServiceVisitsValues, 'weekdays' | 'startDate' | 'endDate'>) {
    const {startDate, endDate} = values
    if (!startDate || !endDate || values.weekdays.length === 0) {
        return []
    }

    const weekdays = new Set(values.weekdays)
    const days: Date[] = []
    for (let day = startDate; !isAfter(day, endDate); day = addDays(day, 1)) {
        if (weekdays.has(jsDayToPythonWeekday(day.getDay()))) {
            days.push(day)
        }
        if (days.length > MAX_VISITS) {
            break
        }
    }
    return days
}

export function validateServiceVisits(values: ServiceVisitsValues): Record<string, string> {
    const errors: Record<string, string> = {}
    if (!values.serviceTypeId) {
        errors['serviceTypeId'] = 'Category is required'
    }
    if (values.weekdays.length === 0) {
        errors['weekdays'] = 'At least one weekday is required'
    }
    if (!values.startDate) {
        errors['startDate'] = 'Start date is required'
    }
    if (!values.endDate) {
        errors['endDate'] = 'End date is required'
    } else if (values.startDate && isBefore(values.endDate, values.startDate)) {
        errors['endDate'] = 'End date must be on or after start date'
    }
    if (Object.keys(errors).length === 0) {
        const days = visitDays(values)
        if (days.length === 0) {
            errors['weekdays'] = 'No visits fall on the selected days'
        } else if (days.length > MAX_VISITS) {
            errors['endDate'] = `Cannot exceed ${MAX_VISITS} visits`
        }
    }
    if (!values.lines.some((line) => line.quantity > 0)) {
        errors['lines'] = 'At least one crew or equipment line is required'
    }
    return errors
}

export function toServiceVisitsInput(
    values: ServiceVisitsValues,
    job: JobDetail,
): CreateServiceScheduleInput | null {
    const {serviceTypeId, startDate, endDate} = values
    if (!serviceTypeId || !startDate || !endDate) {
        return null
    }

    const needed = values.lines.filter((line) => line.quantity > 0)
    return {
        projectId: job.projectId,
        visitsOnly: true,
        serviceTypeId,
        startDate,
        endDate,
        cadenceWeekdays: [...values.weekdays].sort((left, right) => left - right),
        serviceNotes: values.notes.trim() || null,
        serviceWorkerRequirements: needed
            .filter((line) => line.kind === 'worker')
            .map((line) => ({workerTypeId: line.typeId, quantity: line.quantity})),
        serviceAssetRequirements: needed
            .filter((line) => line.kind === 'equipment')
            .map((line) => ({rentalAssetTypeId: line.typeId, quantity: line.quantity})),
        deploy: {workerRequirements: [], assetRequirements: []},
        pickup: {workerRequirements: [], assetRequirements: []},
        locations: job.locations.flatMap((location) =>
            location.latitude != null && location.longitude != null
                ? [
                      {
                          name: location.name,
                          latitude: location.latitude,
                          longitude: location.longitude,
                          notes: location.notes,
                      },
                  ]
                : [],
        ),
        customerContactId:
            job.customerContactSource === 'job' ? (job.customerContact?.id ?? null) : null,
        skippedServiceDates: [],
        poNumber: job.poNumber,
        projectBillingTermsId: job.projectBillingTermsId,
        serviceStartTime: values.visitTime,
        serviceEndTime: null,
        jobsReadyForDispatch: values.readyForDispatch,
    }
}
